import clsx from 'clsx';
import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { MultiSelect, MultiSelectChips, Option } from '../../../components/ui/multi-select';
import type { PipeJobRef } from '../utils/jobNumbersFromSetup';
import {
  parsePipeJobRefKey,
  pipeJobRefKey,
} from '../utils/jobNumbersFromSetup';

export interface WeldFilterSelection {
  /** Per-pipe job selections (source of truth for Apply). */
  jobRefs: PipeJobRef[];
  weldingSystem: string;
  passFamily: string;
  anomalyOnly: boolean;
  dateFrom: string;
  dateTo: string;
}

export type JobOptionGrouped = {
  value: string; // pipeId::jobNumber
  label: string;
  group?: string;
};

interface WeldFilterPanelProps {
  jobOptions: JobOptionGrouped[];
  weldingSystems: string[];
  passFamilies: string[];
  selection: WeldFilterSelection;
  onChange: (next: Partial<WeldFilterSelection>) => void;
  onApply: () => void;
  disabled?: boolean;
  isApplying?: boolean;
  pipeSelected?: boolean;
  filtersDirty?: boolean;
  appliedJobNumbers?: string[];
  /** When false, panel body is hidden (Filters toggle lives on the pipe row). Default false. */
  filtersOpen?: boolean;
  /** Extra controls rendered in the same filter row. Combined uses this for NDT filters. */
  extraFields?: ReactNode;
}

const inlineLabelClass = 'shrink-0 text-xs text-heritage-grey whitespace-nowrap';
const selectClass =
  'w-full min-w-0 rounded-lg border border-[#E2E5EA] bg-white px-2 py-1.5 text-sm text-blueprint-blue focus:border-hero-red-20 focus:outline-none';
const dateClass =
  'w-full min-w-0 rounded-lg border border-[#E2E5EA] bg-white px-2 py-1.5 text-sm text-blueprint-blue focus:border-hero-red-20 focus:outline-none';

function buildJobChipOptions(
  jobOptions: JobOptionGrouped[],
  selectedKeys: string[],
): Option[] {
  const selectedJobCounts = new Map<string, number>();
  selectedKeys.forEach((key) => {
    const ref = parsePipeJobRefKey(key);
    if (!ref?.jobNumber) return;
    selectedJobCounts.set(ref.jobNumber, (selectedJobCounts.get(ref.jobNumber) ?? 0) + 1);
  });

  return jobOptions.map((o) => {
    const ref = parsePipeJobRefKey(o.value);
    const jobLabel = o.label;
    const isDup =
      ref?.jobNumber != null && (selectedJobCounts.get(ref.jobNumber) ?? 0) > 1;
    return {
      value: o.value,
      label: isDup && o.group ? `${jobLabel} · ${o.group}` : jobLabel,
    };
  });
}

export const WeldFilterPanel = ({
  jobOptions,
  weldingSystems,
  selection,
  onChange,
  onApply,
  disabled,
  isApplying = false,
  pipeSelected = true,
  filtersDirty = false,
  filtersOpen = false,
  extraFields = null,
}: WeldFilterPanelProps) => {
  const jobMultiOptions: Option[] = jobOptions.map((o) => ({
    value: o.value,
    label: o.group ? `${o.group} · ${o.label}` : o.label,
  }));
  const selectedKeys = selection.jobRefs.map(pipeJobRefKey);
  const jobChipOptions = buildJobChipOptions(jobOptions, selectedKeys);
  const hasJobs = selection.jobRefs.length > 0;
  const applyDisabled = disabled || !pipeSelected || !hasJobs || isApplying;

  const handleJobKeysChange = (next: string[]) => {
    const refs = next
      .map(parsePipeJobRefKey)
      .filter((r): r is PipeJobRef => r != null);
    onChange({ jobRefs: refs });
  };

  if (!filtersOpen) return null;

  return (
    <div
      className="rounded-lg border border-[#E2E5EA] bg-white px-3 py-2 shadow-sm"
      data-testid="weld-filter-panel"
    >
      <div className="flex flex-nowrap items-center gap-2 overflow-x-auto">
        <div className="flex min-w-[200px] max-w-[280px] shrink-0 items-center gap-1.5">
          <span className={inlineLabelClass}>Job #:</span>
          <div className="min-w-0 flex-1">
            <MultiSelect
              options={jobMultiOptions}
              selected={selectedKeys}
              onChange={handleJobKeysChange}
              placeholder={
                !pipeSelected
                  ? 'Select a pipe size first'
                  : jobOptions.length === 0
                    ? 'No jobs in Setup or Production for selected pipes'
                    : 'Select jobs'
              }
              className="w-full min-w-0"
              chipLayout="wrap"
            />
          </div>
        </div>

        <div className="flex w-[120px] shrink-0 items-center gap-1.5">
          <span className={inlineLabelClass}>System:</span>
          <select
            id="weld-system"
            className={selectClass}
            value={selection.weldingSystem}
            onChange={(e) => onChange({ weldingSystem: e.target.value })}
            disabled={disabled}
          >
            <option value="">All</option>
            {weldingSystems.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="flex w-[140px] shrink-0 items-center gap-1.5">
          <span className={inlineLabelClass}>From:</span>
          <input
            id="weld-from"
            type="date"
            className={dateClass}
            value={selection.dateFrom}
            onChange={(e) => onChange({ dateFrom: e.target.value })}
            disabled={disabled}
          />
        </div>

        <div className="flex w-[140px] shrink-0 items-center gap-1.5">
          <span className={inlineLabelClass}>To:</span>
          <input
            id="weld-to"
            type="date"
            className={dateClass}
            value={selection.dateTo}
            onChange={(e) => onChange({ dateTo: e.target.value })}
            disabled={disabled}
          />
        </div>

        {extraFields ? <div className="flex shrink-0 items-center gap-2">{extraFields}</div> : null}

        <button
          type="button"
          onClick={onApply}
          disabled={applyDisabled}
          className={clsx(
            'ml-auto inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-4 text-sm font-semibold text-white transition',
            applyDisabled
              ? 'cursor-not-allowed bg-slate-300'
              : 'bg-hero-red hover:bg-readable-red',
            filtersDirty && !applyDisabled && 'ring-2 ring-hero-red-20 ring-offset-1',
          )}
        >
          {isApplying ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Applying…
            </>
          ) : (
            'Apply'
          )}
        </button>
      </div>

      {selectedKeys.length > 0 ? (
        <div className="mt-2 min-w-0 w-full">
          <MultiSelectChips
            options={jobChipOptions}
            selected={selectedKeys}
            layout="ellipsis"
            onRemove={(value) =>
              handleJobKeysChange(selectedKeys.filter((k) => k !== value))
            }
            className="min-w-0 w-full"
          />
        </div>
      ) : null}
    </div>
  );
};
